import React from 'react';

import {
  faSquare as notSelected
} from '@fortawesome/free-regular-svg-icons';
import {
  faCheckSquare as selected,
  faCheck as check,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {map, get, orderBy, reject, has} from 'lodash-es';
import moment from 'moment';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router';
import {bindActionCreators} from 'redux';

import MessageCta from 'components/MessageCta';
import {clearModal} from 'modulesV2/actions';
import {ButtonWithTrackEvent} from 'static/three-oh/src/components/Button/Button';
import uiColors from 'static/three-oh/src/components/Colors/colors.js';
import {
  messageLevels,
  transitionDirections,
} from 'static/three-oh/src/components/Message';
import snackBarStyles from 'static/three-oh/src/components/Message/styles.scss';
import StyledModal from 'static/three-oh/src/components/Modal/StyledModal/StyledModal';
import Pill from 'static/three-oh/src/components/Pill';
import TrackedLink from 'static/three-oh/src/components/TrackedLink';
import {lyceumOrigin} from 'static/three-oh/src/constants/lyceum';
import {stringifyParams} from 'static/three-oh/src/modules/apis/ApiFetcher2';
import fetcher3 from 'static/three-oh/src/modules/apis/ApiFetcher3';
import {getUserProperties} from 'static/three-oh/src/modules/selectors/userSelectors';
import {getModalId, getModalOptions} from 'static/three-oh/src/modulesV2/selectors';
import AngelouProps from 'static/three-oh/src/propTypes/AngelouPropTypes';
import {toSnakeCase} from 'static/three-oh/utils/stringUtils';
import withV3AssignmentApiGet from 'static/three-oh/utils/withV3AssignmentApiGet';

import styles from './styles.scss';


const propTypesAssignButtonModal = {
  ...AngelouProps,
  assignmentsApiCallState: PropTypes.object,
  clearModal: PropTypes.func,
  createMessage: PropTypes.func,
  createdFrom: PropTypes.string,
  history: PropTypes.object,
  modalId: PropTypes.string,
  contentIds: PropTypes.arrayOf(PropTypes.string),
  isFreeUser: PropTypes.bool,
  retrieveAssignmentsApiCallFunc: PropTypes.func,
  user: PropTypes.object,
};

class AssignButtonModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      modalIsOpen: false,
      selectedAssignments: {},
      updatePending: false,
    };

    this.closeModal = this.closeModal.bind(this);
    this.renderModal = this.renderModal.bind(this);
    this.toggleAssignmentSelect = this.toggleAssignmentSelect.bind(this);
    this.updateAssignmentsWithContent = this.updateAssignmentsWithContent.bind(this);
  }

  componentDidUpdate(prevProps) {
    this.checkModalStatus(prevProps);
  }

  checkModalStatus(prevProps) {
    if (this.props.modalId !== prevProps.modalId) {
      const shouldBeOpen = this.props.modalId === 'AddContentToAssignmentModal';
      if (shouldBeOpen && this.props.assignmentsApiCallState.isLoading === undefined) {
        this.props.retrieveAssignmentsApiCallFunc();
      }
      this.setState({modalIsOpen: shouldBeOpen});
    }
  }

  selectionHasDrafts() {
    const assignments = map(this.state.selectedAssignments, (assignment) => {
      return assignment.date_assigned === null;
    });

    return assignments.includes(true);
  }

  formatAssignmentPayload = (assignment) => {
    const postMemberFormat = assignment.assignment_members.map((member) => {
      return {...member, content_id: member.content.content_id};
    });
    delete assignment.assignment_members;
    assignment.assignment_members = postMemberFormat;

    const postClassroomFormat = assignment.assignment_classrooms.map((classroom) => {
      return {...classroom, classroom_id: classroom.classroom.id, date_assigned: classroom.date_assigned};
    });
    delete assignment.assignment_classrooms;
    assignment.assignment_classrooms = postClassroomFormat;

    assignment.source_content_id = assignment.source_content?.content_id;
    delete assignment.source_content;

    delete assignment.student_assignment_view_records;

    // Remove null objects to prevent API post from failing.
    Object.keys(assignment).forEach((key) => {
      if (assignment[key] === null) delete assignment[key];
    });

    return assignment;
  };

  updateAssignmentsWithContent() {
    this.setState({updatePending: true});

    // Add the content(s) to each selected assignment.
    const requests = map(this.state.selectedAssignments, async(assignment) => {
      const assignmentContentIds = assignment?.assignment_members.map((assignmentMember) => assignmentMember.content.content_id);
      this.props.contentIds.forEach((contentId, i) => {
        // Only add the content to the assignment if it is not already included
        // in the assignment, and, if the user is licensed to assign the content.
        const okToAddMemberToAssignment = !assignmentContentIds.includes(contentId);

        if (okToAddMemberToAssignment) {
          assignment.assignment_members.push({
            content: {content_id: contentId},
            display_order: assignment.assignment_members.length + i
          });
        }
      });

      const assignmentPayload = this.formatAssignmentPayload(assignment);

      const request = await fetcher3.post(`assignment/${assignment.content_id}/`, {data: assignmentPayload});
      return request;
    });



    const handleSuccess = () => {
      const msg = (
        <span
          className={snackBarStyles.successMsg}
          data-qa-selector="success_message"
        >
          <FontAwesomeIcon icon={check}/>
          Content Added
          <TrackedLink
            href="/binder/assignments"
            eventName="navigate-from-add-article-success"
          >
            View Assignments
          </TrackedLink>
        </span>
      );

      return this.setState({
        modalIsOpen: false,
        updatePending: false,
        selectedAssignments: {}
      }, () => {
        this.props.dispatch({
          type: 'CREATE_MESSAGE',
          data: {
            text: msg,
            status: 'success',
          }
        });
        this.props.clearModal();
      });
    };

    const handleError = () => {
      return this.setState({
        updatePending: false
      },
      () => this.props.dispatch({
        type: 'CREATE_MESSAGE',
        data: {
          text: 'Your assignment(s) failed to update. Please try again later.',
          status: 'danger',
        }
      }));
    };

    Promise.all(requests).then(handleSuccess).catch(handleError);
  }

  async navigateToCreateAssignment() {
    const {clearModal, history} = this.props;
    const urlParams = {
      return_url: history.location.pathname + history.location.search,
      createdFrom: this.props.contentId,
      contentIds: this.props.contentIds
    };
    const params = stringifyParams(urlParams);

    clearModal();
    window.location.href = `${lyceumOrigin}/assignments/create${params}`;
  }

  toggleAssignmentSelect(assignment, isAlreadyAssigned) {
    // The assignment should still be selectable if some articles from a
    // text set are already in that assignment.
    if (isAlreadyAssigned && !this.props.createdFrom) { return; }
    if (this.state.selectedAssignments[assignment.content_id]) {
      // remove
      const removedFromList = this.state.selectedAssignments;
      delete removedFromList[assignment.content_id];
      this.setState({selectedAssignments: removedFromList});
    } else {
      // add
      const addedToList = this.state.selectedAssignments;
      addedToList[assignment.content_id] = assignment;
      this.setState({selectedAssignments: addedToList});
    }
  }

  closeModal() {
    this.setState({selectedAssignments: {}}); // Reset to initial state.
    this.props.clearModal();
  }

  renderModalButtons() {
    const tracking = {
      objectType: 'Assignment',
      eventProperties: {
        content_id: this.props.createdFrom,
        content_ids: this.props.contentIds,
        selectedAssignments: this.state.selectedAssignments,
        additionalObjectType: this.selectionHasDrafts() ? 'draft' : '',
        description: 'Add content to assignments'
      },
      streamProperties: {
        description: 'Add content to assignment(s).',
        additional_object_type: this.selectionHasDrafts() ? 'draft' : '',
        content_id: this.props.createdFrom,
        content_ids: this.props.contentIds,
      }
    };

    const renderCancelButton = () => (
      <ButtonWithTrackEvent
        standard
        secondary
        key="cancel"
        label="Cancel"
        onClick={this.closeModal}
        actionPrefix="Cancel"
        actionName="Update"
        legacyEventName="Assignment-ExitAddHeaderModal"
        {...tracking}
      />
    );

    if (this.hasAssignments()) {
      const assignmentsSelected = Object.keys(this.state.selectedAssignments).length;
      const buttonText = assignmentsSelected > 1 ? `Add to ${assignmentsSelected} Assignments` : 'Add to Assignment';

      return (
        <>
          {renderCancelButton()}
          <ButtonWithTrackEvent
            standard
            primary
            disabled={assignmentsSelected <= 0}
            key="add"
            label={buttonText}
            onClick={this.updateAssignmentsWithContent}
            actionName="Update"
            legacyEventName="Assignment-Update"
            {...tracking}
          />
        </>
      );
    } else {
      return (
        <>
          {renderCancelButton()}
          <ButtonWithTrackEvent
            standard
            primary
            key="create"
            label="Create Assignment"
            onClick={() => this.navigateToCreateAssignment()}
            actionPrefix="Initiate"
            actionName="Create"
            legacyEventName="Assignment-InitiateCreate"
            {...tracking}
          />
        </>
      );
    }
  }

  hasAssignments() {
    return (
      this.props.assignmentsApiCallState?.data?.length > 0
    );
  }

  isAlreadyAssigned(assignment) {
    // Check to see if any of the content attempting to be assigned already
    // belongs to the selected assignment.
    const assignmentContentIds = assignment?.assignment_members?.map((assignmentMember) => assignmentMember?.content?.content_id);
    const already = assignmentContentIds.some((contentId) => this.props.contentIds?.includes(contentId));
    return already;
  }

  renderAssignmentDate(isAlreadyAssigned, date, isDraft) {
    if (isAlreadyAssigned && this.props.createdFrom) {
      return (
        <Pill
          backgroundColor={uiColors.grey_light[0]}
          textColor={uiColors.white[0]}
          text="Some content may already be added"
        />
      );
    } else if (isAlreadyAssigned) {
      return (
        <Pill
          backgroundColor={uiColors.grey_light[0]}
          textColor={uiColors.white[0]}
          text="Already Added"
        />
      );
    } else {
      if (isDraft) { // Future dates get highlighted background.
        return (
          <Pill
            backgroundColor={uiColors.yellow[0]}
            textColor={uiColors.grey_dark[0]}
            text="Draft"
          />
        );
      } else {
        return (
          <Pill
            backgroundColor={uiColors.grey_light[0]}
            textColor={uiColors.white[0]}
            text={date ? `Assigned on ${moment(date).format('MM/DD/YY')}` : 'Assigned'}
          />
        );
      }
    }
  }

  renderModalBody() {
    if (this.props.assignmentsApiCallState.isLoading || this.state.updatePending) {
      return <div className={styles.loading}><i className="fa fa-spinner fa-spin" /></div>;
    }

    if (this.hasAssignments()) {
      const assignments = orderBy(this.props.assignmentsApiCallState.data, (assignment) => {
        return new Date(assignment.date_modified);
      }, 'desc');

      const premiumHeadersRemovedMessage = (
        <span>
          Only articles for which you have a license will be added: the others will be removed.
        </span>
      );
      const premiumHeadersRemoved = (
        this.props.isFreeUser &&
        !!reject(this.props.articleHeaders, ['requires_license', false]).length
      );

      return (
        <>
          <p
            className={styles.modalInstructions}
            data-qa-selector="modal_instructions"
          >
            Add this content to one or more of your existing assignments.
          </p>
          {
            premiumHeadersRemoved &&
            <MessageCta
              level={messageLevels.info}
              text={premiumHeadersRemovedMessage}
              ctaText={null}
              transitionDirection={transitionDirections.DOWN}
              key={`pro_headers_were_removed_${this.props.createdFrom}`}
              additionalClassnames={[styles.proHeadersRemovedMessage]}
              hideCloseIcon
            />
          }
          <ul
            className={styles.list}
            data-qa-selector="list"
          >
            {
              assignments.map((assignment, index) => {
                const isAlreadyAssigned = this.isAlreadyAssigned(assignment);
                // If articles from a text set are in the assignment, the user should still have the option to add the text set.
                const isSelected = has(this.state.selectedAssignments, assignment.content_id) || (isAlreadyAssigned && !this.props.createdFrom);
                return (
                  <li key={`assignment-${assignment.content_id}`}>
                    <div
                      className={`${styles.assignmentRow} ${(isAlreadyAssigned && !this.props.createdFrom) ? styles.already : ''}`}
                      onClick={() => this.toggleAssignmentSelect(assignment, isAlreadyAssigned)}
                      data-qa-selector={`assigned_toggle_${index}`}
                    >
                      <div><FontAwesomeIcon icon={isSelected ? selected : notSelected}/></div>
                      <div className={styles.assignmentInfo}>
                        <div
                          className={styles.assignmentTitle}
                          data-qa-selector={toSnakeCase(assignment.title)}
                        >{assignment.title}</div>
                        {this.renderAssignmentDate(isAlreadyAssigned, assignment?.assignment_classrooms[0]?.date_assigned, assignment?.is_draft)}
                      </div>
                    </div>
                  </li>
                );
              })
            }
          </ul>
        </>
      );
    } else {
      return (
        <p
          className={styles.modalInstructions}
          data-qa-selector="modal_instructions"
        >You currently have no assignments. Would you like to create one?</p>
      );
    }
  }

  renderModal() {
    return (
      <StyledModal
        open={this.state.modalIsOpen}
        onClose={this.closeModal}
        title="Add content to Assignment"
        body={this.renderModalBody()}
        scrollBody
        footer={this.renderModalButtons()}
        centered
        disableFocusTrap={false}
        data-qa-selector="add_to_assignment"
      />
    );
  }

  render() {
    if (this.props.user.is_school_educator) {
      return (
        <>
          {this.renderModal()}
        </>
      );
    } else {
      return null;
    }
  }
}

AssignButtonModal.propTypes = propTypesAssignButtonModal;
AssignButtonModal.defaultProps = {
  primary: true,
  rounded: true,
};

const MapStateToProps = (state) => {
  return {
    createdFrom: get(getModalOptions(state), 'createdFrom'),
    contentIds: get(getModalOptions(state), 'contentIds'),
    modalId: getModalId(state),
    history: get(getModalOptions(state), 'history'),
    isFreeUser: getUserProperties(state).isFreeUser,
    user: state.user,
  };
};

const actions = {
  clearModal,
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(actions, dispatch),
  };
}

export default connect(MapStateToProps, mapDispatchToProps)(withRouter(withV3AssignmentApiGet(AssignButtonModal)));
